import { useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Button } from 'antd'
import * as Icons from '@ant-design/icons'
import produce from 'immer'
import _ from 'lodash'
import { useFormContext } from 'react-hook-form'

import {
  AddNewItem,
  CreationDate,
  Form,
  FormSectionTitle,
  FormSectionWrapper,
  SectionProvider
} from 'admin/lib/Forms'
import { HandleSingleAndDoubleClick } from 'admin/lib/Functions/handleSingleAndDoubleClick'
import {
  getLocaleFromCountry,
  isValidTimeZone
} from 'admin/lib/Functions/timeZoneHelper'
import { Flag } from 'admin/lib/Icons/Flag'

import { WasteLineItem } from './WasteLineItem'
import { WasteData } from './WasteData'
import {
  AddMultipleLineItemsModal,
  DuplicateRecordControls,
  FormControls,
  LastModifiedBy,
  RecordHistory,
  focusFirstError,
  useLineItemsManagement,
  AttachmentView,
  useExtractionValueClicked,
  TemplateNameLabel,
  AutoPublishFailureReason
} from 'admin/pages/RecordEdit/common'
import {
  isoDateFromFormDate,
  isoDateToFormDate,
  cleanDate
} from 'admin/lib/dates'

const DEFAULT_ITEM = Object.freeze({
  id: null,
  containerType: '',
  knownContainerType: '',
  wasteCode: '',
  volumeEstimated: '',
  volume: '',
  volumeUoM: 'CubicMeter',
  weightEstimated: '',
  weight: '',
  weightUoM: 'Tonne',
  quantity: 1
})

const DEFAULT_ITEM_IMPERIAL = Object.freeze({
  ...DEFAULT_ITEM,
  volumeUoM: 'CubicYard',
  weightUoM: 'Pound'
})

const DEFAULT_RAW_ITEM = Object.freeze({
  id: null,
  containerType: '',
  wasteCode: '',
  volume: '',
  weight: '',
  quantity: ''
})

export function Waste({
  record,
  submit,
  project,
  isImperial,
  showHistory,
  onHistoryCloseClick
}) {
  const [showDebug, setShowDebug] = useState(false)
  const [isAddMultipleLineItemsOpen, setIsAddMultipleLineItemsOpen] =
    useState(false)

  const form = useFormContext()
  const { moduleName, projectId, recordId, accountId } = useParams()
  const { reset, setValue } = form

  const [lineItemErrors, setLineItemErrors] = useState({})

  const projectCountry = project.country
  const projectTimeZone = project.timeZone ?? 'Europe/London'
  const projectLocale = getLocaleFromCountry(projectCountry)

  const MULTIATTACHMENT_WARNING_MESSAGE =
    'Multiple images uploaded with this record'

  const {
    methods,
    lineItemDuplicateHandlers,
    lineItemRemoveHandlers,
    handleAppendLineItem,
    handleAppendLineItemNoDebounce
  } = useLineItemsManagement(
    form,
    'data.items',
    'raw.items',
    isImperial ? DEFAULT_ITEM_IMPERIAL : DEFAULT_ITEM,
    DEFAULT_RAW_ITEM
  )

  const focusOnNewItem = () => {
    // focus on new element when ready
    const waitForInterval = setInterval(() => {
      const element = document.querySelector(
        `textarea[name^="uniquePrefix[${methods.fields.length}].containerType"`
      )
      if (element !== null) {
        clearInterval(waitForInterval)
        element.focus()
        const form = document.getElementsByTagName('form')[0]
        form.scrollTo({
          top: form.scrollHeight,
          behavior: 'smooth'
        })
      }
    }, 500)
  }

  const openAddNewItemsModal = e => {
    setIsAddMultipleLineItemsOpen(true)
  }

  const closeAddNewItemsModal = e => {
    setIsAddMultipleLineItemsOpen(false)
  }

  const addNewLineItem = e => {
    handleAppendLineItem()
    focusOnNewItem()
  }

  const addMultipleNewLineItems = numOfNewItems => {
    if (numOfNewItems > 0) {
      for (let i = 1; i <= numOfNewItems; i++) {
        handleAppendLineItemNoDebounce()
      }
    }
  }

  const handleAddNewLineItem = HandleSingleAndDoubleClick(
    addNewLineItem,
    openAddNewItemsModal
  )

  const duplicateLineItem = item => {
    lineItemDuplicateHandlers(item)
    focusOnNewItem()
  }

  const duplicateLineItemMultiple = (numOfNewItems, originalItem) => {
    if (numOfNewItems > 0) {
      for (let i = 1; i <= numOfNewItems; i++) {
        lineItemDuplicateHandlers(originalItem)
      }
    }
  }

  useEffect(() => {
    reset(
      produce(record, draft => {
        draft.data.removalDate = cleanDate(draft.data.removalDate)
        draft.data.removalDate = isoDateToFormDate(
          draft.data.removalDate,
          projectTimeZone
        )
        draft.data.removalExitDate = cleanDate(draft.data.removalExitDate)
        draft.data.removalExitDate = isoDateToFormDate(
          draft.data.removalExitDate,
          projectTimeZone
        )
      })
    )
  }, [projectTimeZone, record, reset])

  const extractionValueClicked = useExtractionValueClicked(setValue)

  const toggleDebug = e => {
    if (e.detail === 3) {
      setShowDebug(s => !s)
    }
  }

  return (
    <SectionProvider style={{ gap: '15px' }}>
      <Form
        id="form-panel"
        onSubmit={form.handleSubmit(
          formRecord => {
            if (Object.keys(lineItemErrors).length > 0) {
              focusFirstError(form.errors, lineItemErrors)
              return
            }

            submit(trackAndProduceRecord(record, formRecord, projectTimeZone))
          },
          () => focusFirstError(form.errors, lineItemErrors)
        )}
      >
        {record.recordSimilarityDetails && (
          <DuplicateRecordControls
            recordSimilarityDetails={record.recordSimilarityDetails}
            recordStatus={record.status}
          />
        )}

        <TemplateNameLabel
          templateName={record.templateName}
          estimatedAccuracy={record.estimatedAccuracy}
          matchedRecord={record.matchedRecord}
          moduleName={moduleName}
        />

        <AutoPublishFailureReason
          autoPublishFailureReasons={record.autoPublishFailureReasons}
        />

        {record.attachmentPages > 1 && (
          <p className="warning">{MULTIATTACHMENT_WARNING_MESSAGE}</p>
        )}
        <FormControls />
        <LastModifiedBy
          modifier={record.modifier}
          modifiedById={record.modifiedBy}
        />
        {record.migrationSource !== null && (
          <p>
            Migrated from recordId: {record.migrationSource.recordId}
            <br />
            Migration Reason: {record.migrationSource.migrationReason}
          </p>
        )}

        <FormSectionWrapper>
          <FormSectionTitle level={5} onClick={toggleDebug}>
            <Flag country={projectCountry} /> Data
          </FormSectionTitle>
          <CreationDate>
            <>
              <div style={{ fontWeight: '600' }}>Creation Date</div>
              <div style={{ lineHeight: '1.2', margin: '4px 0' }}>
                {`${
                  projectTimeZone &&
                  isValidTimeZone(projectTimeZone) &&
                  Intl.DateTimeFormat().resolvedOptions().timeZone !==
                    projectTimeZone
                    ? 'Local: '
                    : ''
                }${new Date(record.createdAt).toLocaleString([], {
                  timeZoneName: 'short',
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric'
                })}`}
                {
                  // If Project Locale differs from local Locale
                  // Or project TimeZone differs from local TimeZone
                  (projectLocale !==
                    Intl.DateTimeFormat().resolvedOptions().locale ||
                    (projectTimeZone &&
                      isValidTimeZone(projectTimeZone) &&
                      Intl.DateTimeFormat().resolvedOptions().timeZone !==
                        projectTimeZone)) && (
                    <>
                      <br />
                      Project:{' '}
                      {new Date(record.createdAt).toLocaleString(
                        projectLocale,
                        {
                          timeZone: projectTimeZone,
                          timeZoneName: 'short',
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: 'numeric',
                          minute: 'numeric'
                        }
                      )}
                    </>
                  )
                }
              </div>
            </>
          </CreationDate>
        </FormSectionWrapper>
        {showDebug && (
          <div
            style={{ marginBottom: '5px', fontSize: 'smaller' }}
            tabindex="-1"
          >
            <dl>
              <dt>Journey distance (mi)</dt>
              <dd>{record.data.journey?.distanceMiles}</dd>
            </dl>
            <dl>
              <dt>
                Laden journey emissions (kg CO<sub>2</sub>e)
              </dt>
              <dd>{record.data.journey?.ladenEmissionsKgCO2e}</dd>
            </dl>
            <dl>
              <dt>
                Unladen journey emissions (kg CO<sub>2</sub>e)
              </dt>
              <dd>{record.data.journey?.unladenEmissionsKgCO2e}</dd>
            </dl>
          </div>
        )}
        <WasteData
          createdAt={record.createdAt}
          projectName={project.name}
          projectTimeZone={projectTimeZone}
          projectCountry={project.country}
          projectId={projectId}
          isImperial={isImperial}
        />
        <FormSectionWrapper>
          <FormSectionTitle level={5}>
            <Flag country={projectCountry} /> Items (
            {
              methods.fields.filter(item => !item.isDeleted && !item.isRemoved)
                .length
            }
            )
          </FormSectionTitle>
          <AddNewItem onClick={handleAddNewLineItem}>Add new item</AddNewItem>
        </FormSectionWrapper>
        {methods.fields.map((item, i) => (
          <WasteLineItem
            key={item.hookFormId}
            index={i}
            item={item}
            onDuplicate={duplicateLineItem}
            onDuplicateMultiple={duplicateLineItemMultiple}
            onRemove={lineItemRemoveHandlers[i]}
            setLineItemErrors={setLineItemErrors}
          />
        ))}
        <Button
          onClick={handleAddNewLineItem}
          block
          icon={<Icons.PlusCircleFilled />}
          type="dashed"
          htmlType="button"
        >
          Add new item
        </Button>

        <Button
          onClick={e => (document.getElementById('form-panel').scrollTop = 0)}
          style={{ marginTop: '1rem' }}
        >
          Back to top
        </Button>
      </Form>

      <AttachmentView
        record={record}
        handleExtractionValueClicked={extractionValueClicked}
      />
      {record.history && (
        <RecordHistory
          changes={record.history.changes}
          moduleName={moduleName}
          isImperial={isImperial}
          projectLocale={projectLocale}
          projectTimeZone={projectTimeZone}
          lineItemCount={record.data.items.length}
          onCloseClick={onHistoryCloseClick}
          showHistory={showHistory}
          accountId={accountId}
          projectId={projectId}
          recordId={recordId}
        />
      )}

      <AddMultipleLineItemsModal
        moduleName={moduleName}
        isOpen={isAddMultipleLineItemsOpen}
        onClose={closeAddNewItemsModal}
        onSubmit={val => {
          addMultipleNewLineItems(val)
        }}
        duplicateLineItemName={null}
      />
    </SectionProvider>
  )
}

Waste.propTypes = {
  record: PropTypes.object,
  submit: PropTypes.func.isRequired
}

function trackAndProduceRecord(record, formRecord, projectTimeZone) {
  return produce(_.merge({}, record, formRecord), draft => {
    draft.data.removalDate = isoDateFromFormDate(
      draft.data.removalDate,
      projectTimeZone
    )
    draft.data.removalExitDate = isoDateFromFormDate(
      draft.data.removalExitDate,
      projectTimeZone
    )
    draft.data.items = formRecord.data.items
    draft.assignee = formRecord.assignee
  })
}
